import { useUiStore } from '@/data';
import { Container, Heading, Image, Link, useData } from '@/components';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import style from './megamenu.module.scss';
import { PAGE_ROUTES } from '@/constants';
import { FC } from 'react';
import * as prismicH from '@prismicio/helpers';
import { asText } from '@prismicio/client';

interface IMegaMenuProps {
  itemID: string;
  setMenuItemID: (_id: string | null) => void;
}

const Megamenu: FC<IMegaMenuProps> = ({ itemID, setMenuItemID }) => {
  const { navigation } = useData();
  const [, setState] = useUiStore();

  if (!itemID) {
    return <></>;
  }

  const megaNav: any = navigation?.data?.links.find((item: any) => {
    const { uid, type } = item.external_link;
    return uid === itemID && type === 'mega_nav';
  });

  if (!megaNav) {
    return <></>;
  }

  // megaNavBody is any because Prismic does not generate
  // the type for the external_link type
  const megaNavBody: any = megaNav?.external_link?.data?.body;

  const firstSection =
    megaNavBody.filter(item => item?.slice_type === 'first_section')[0] || [];

  const megaMenuNavLinks =
    megaNavBody.filter(item => item?.slice_type === 'group_link') || [];

  const megaMenuNavLinksSorted = sortBy(
    megaMenuNavLinks,
    o => o?.primary?.sort,
  );

  const megaMenuPictureSections =
    megaNavBody.filter(item => item?.slice_type === 'picture_section')[0] || [];

  const handleCloseMegaMenu = () => {
    setState({ is_megamenu_open: false });
    setMenuItemID(null);
  };

  const generateNav = (link, index) => {
    const label = asText(link?.primary?.group_link_label) || undefined;
    return (
      <nav aria-label={label} key={index}>
        <Heading className={style.head} level={6}>
          {prismicH.asText(link?.primary?.group_link_label)}
        </Heading>
        <ul>
          {link.items.map((item, i) => {
            return (
              <li key={i}>
                <Link
                  aria-label={`Go to ${item?.label}`}
                  href={item?.link}
                  onClick={handleCloseMegaMenu}
                >
                  {item?.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  };

  const generateLink = (item, index) => {
    return (
      <Link
        aria-label={`Go to ${item?.label}`}
        href={item?.link}
        key={index}
        onClick={handleCloseMegaMenu}
      >
        {item?.label}
      </Link>
    );
  };

  return (
    // Mouse-leave closes the panel for pointer users only. Not an interactive
    // control (no menu/button role) — keyboard dismiss is handled by the parent nav.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <section
      aria-label="Mega menu"
      className={classNames(style.megamenu)}
      onMouseLeave={handleCloseMegaMenu}
    >
      <Container className={style.megamenu__container}>
        <nav className={style['section-one']}>
          {firstSection?.items?.map((item, index) => generateLink(item, index))}
          <Link
            aria-label={'Go to shop'}
            className={style.button_link}
            href={PAGE_ROUTES.SHOP}
            onClick={handleCloseMegaMenu}
          >
            Shop all
          </Link>
        </nav>

        <div className={style['section-two']}>
          {megaMenuNavLinksSorted.map((link, index) => {
            if (link?.primary?.sort < 3) {
              return generateNav(link, index);
            }
          })}
        </div>
        <div className={style['section-three']}>
          {megaMenuNavLinksSorted.map((link, index) => {
            if (link?.primary?.sort > 2) {
              return generateNav(link, index);
            }
          })}
        </div>
        <div className={style['section-four']}>
          <Link
            aria-label={`Go to ${
              megaMenuPictureSections?.primary?.title[0]?.text ||
              'promotional content'
            }`}
            href={megaMenuPictureSections?.primary?.link}
            onClick={handleCloseMegaMenu}
          >
            <figure>
              <Image
                alt={megaMenuPictureSections?.primary?.image?.alt ?? 'Picture'}
                fill
                src={megaMenuPictureSections?.primary?.image?.url}
                style={{ objectFit: 'cover', objectPosition: 'right' }}
              />
            </figure>
            <div>
              <p className={style.title}>
                {megaMenuPictureSections?.primary?.title[0]?.text}
              </p>
              <p className={style.subtitle}>
                {megaMenuPictureSections?.primary?.description}
              </p>
            </div>
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default Megamenu;
